import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AWTriggers, Roles } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';

interface Props {
  profileName: string | undefined;
  metaText?: string;
  role: Roles;
  savedSuccessful: boolean | null;

  analyzeText(awTrigger: AWTriggers, isProfiling?: boolean): () => void;

  handleSaveCurrentText(finished: boolean): () => void;

  isAnalysisPossible(): boolean;

  analyzeTextQuick(): void;
}

const EditorActionBar: React.FC<Props> = ({
  profileName,
  metaText,
  role,
  savedSuccessful,
  analyzeText,
  handleSaveCurrentText,
  isAnalysisPossible,
  analyzeTextQuick,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const downXS = useMediaQuery(theme.breakpoints.down(700));

  return (
    <div className={classes.headerCard}>
      <div className={classes.titleBlock}>
        {!_.isNil(profileName) ? <Typography className={classes.title}>{profileName}</Typography> : null}
        {!_.isNil(metaText) ? <Typography className={classes.meta}>{metaText}</Typography> : null}
      </div>
      <Stack className={classes.actions} direction={'row'} alignItems={'center'} gap={'12px'}>
        {role === Roles.ADMIN ? (
          <Button className={classes.outlineButton} onClick={analyzeText(AWTriggers.Manual, true)} disabled={!isAnalysisPossible()}>
            {t('profiling')}
          </Button>
        ) : null}
        <Button className={classes.outlineButton} onClick={analyzeTextQuick} disabled={!isAnalysisPossible()}>
          {downXS ? <AutoFixHighIcon className={classes.buttonIcon} /> : t('autoOptimize')}
        </Button>
        <Button
          className={cx(classes.outlineButton, savedSuccessful ? 'success' : null)}
          onClick={handleSaveCurrentText(false)}
          disabled={!isAnalysisPossible()}
        >
          {downXS ? <SaveIcon className={classes.buttonIcon} /> : t('save')}
        </Button>
        <PaiButton
          text={t('analyzeTextButtonTextShort')}
          action={analyzeText(AWTriggers.Manual)}
          disabled={!isAnalysisPossible()}
          noLineBreak={true}
          style={{ height: '40px', padding: '0 22px', borderRadius: '8px', fontSize: '0.9375rem' }}
        />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  headerCard: {
    width: '100%',
    background: '#FFF',
    borderRadius: '12px',
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    [theme.breakpoints.down(700)]: {
      flexWrap: 'wrap',
      padding: '12px 16px',
    },
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    [theme.breakpoints.down(700)]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  meta: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  actions: {
    marginLeft: 'auto',
  },
  outlineButton: {
    height: '40px',
    padding: '0 16px',
    minWidth: 'unset',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: theme.palette.greys.dark,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: '#FFF',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '&.success': {
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      borderColor: theme.palette.greys.lighter,
    },
  },
  buttonIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
}));

export default EditorActionBar;
